import { toast } from "react-toastify";

import BulkAction from "common/components/BulkAction";
import CampaignTypeIcon from "common/components/CampaignTypeIcon";
import { CAMPAIGN_TYPES } from "common/constants";
import languages from "common/constants/languages";
import { typedObjectEntries } from "common/helpers/utils";
import type {
  Campaign,
  DripFilter,
  Schedule,
  ScheduleInterval,
} from "common/types";

import { CAMPAIGN_OUTREACH_OPTIONS } from "./constants";

import type {
  AudienceProfile,
  CampaignFlow,
  CampaignFlowType,
  DripFilters,
  DripTargeting,
} from "./types";

export function getLanguageName(code: string) {
  return languages.find((lang) => lang.code === code)?.name;
}
export function getFlowsByType(flows: CampaignFlow[]) {
  const primaryFlows = flows.filter(
    (flow) =>
      flow.type === "PRIMARY_CONNECT" || flow.type === "PRIMARY_MESSAGE",
  );

  const connectFlow: CampaignFlow = null;

  const inmailFlow = flows.find((flow) => flow.type === "INMAIL_OPTIMIZATION");

  return { primaryFlows, connectFlow, inmailFlow };
}

export function getSortedAndFilteredFlows(
  flows: CampaignFlow[],
  isInMailOptimizationEnabled: boolean,
): CampaignFlow[] {
  if (!flows) return;

  const filteredFlows = flows.filter((flow) => {
    if (!isInMailOptimizationEnabled && flow.type === "INMAIL_OPTIMIZATION") {
      return false;
    }
    return true;
  });

  const { primaryFlows, connectFlow, inmailFlow } =
    getFlowsByType(filteredFlows);

  const sortedAndFilteredFlows = [
    ...primaryFlows,
    connectFlow,
    inmailFlow,
  ].filter((flow) => flow);

  return sortedAndFilteredFlows;
}

export function validateFlows(
  flows: CampaignFlow[],
  isInMailOptimizationEnabled: boolean,
) {
  const flowsWithError: CampaignFlow[] = [];
  const sortedAndFilteredFlows = getSortedAndFilteredFlows(
    flows,
    isInMailOptimizationEnabled,
  );

  sortedAndFilteredFlows.forEach((flow) => {
    if (
      (flow.type === "INMAIL_OPTIMIZATION" && !flow.template?.original_id) ||
      (flow.type === "PRIMARY_MESSAGE" &&
        !flow.flow_message_templates.some((message) => message.original_id))
    ) {
      flowsWithError.push(flow);
    }
  });

  const isFlowListValid = !flowsWithError.length;

  return { flowsWithError, isFlowListValid };
}

export function isValidSchedule(schedule: Schedule) {
  return Object.values(schedule).some(({ intervals }) => intervals.length > 0);
}

export function getFormattedInterval({ start, end }: ScheduleInterval) {
  const startValue = `${start <= 9 ? `0${start}` : start}:00`;

  let endValue: string;
  if (end === 24) {
    endValue = "23:59";
  } else {
    endValue = `${end <= 9 ? `0${end}` : end}:00`;
  }

  return { startValue, endValue };
}

export function getFlowTitle(flow: CampaignFlow) {
  const flowHeadingOptions: Record<CampaignFlowType, string> = {
    CONNECT_OPTIMIZATION: "Connect Optimization",
    INMAIL_OPTIMIZATION: "Free InMail",
    PRIMARY_MESSAGE: flow.name,
    PRIMARY_CONNECT: flow.name,
  };

  return flowHeadingOptions[flow.type];
}

export function exportProfilesToCSV(profiles: AudienceProfile[]) {
  // Handle empty array case
  if (!profiles || profiles.length === 0) {
    return "#";
  }
  const columns = [
    "id",
    "full_name",
    "url",
    "occupation",
    "company",
    "location",
  ];

  let csvContent = `${columns.join(",")}\n`;

  profiles.forEach((profile) => {
    // Wrap row in quotes to handle commas
    const row = [
      `"${profile.id}"`,
      `"${profile.full_name}"`,
      `"https://linkedin.com/in/${profile.public_identifier}"`,
      `"${profile.occupation}"`,
      `"${profile.company}"`,
      `${profile.location ? `"${profile.location.name}"` : ""}`,
    ].join(",");

    csvContent += `${row}\n`;
  });

  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const downloadUrl = URL.createObjectURL(blob);

  return downloadUrl;
}

// returns parsed filters for server requests
export function parseDripFilters(filters: DripFilters): DripTargeting {
  const { keywords, labels, locations, ...rest } = filters;

  const modifiedLocations = locations.flatMap((location) => {
    const ids = Array.isArray(location.id) ? location.id : [location.id];

    return ids.map((id) => ({
      id,
      name: location.name,
    }));
  });

  return {
    keywords,
    labels: labels as DripFilter[],
    locations: modifiedLocations,
    ...(rest as Omit<DripTargeting, "locations" | "labels" | "keywords">),
  };
}

export function startCampaignsBulkAction(
  actionFn: (campaign: Campaign, abortSignal: AbortSignal) => Promise<void>,
  campaigns: Campaign[],
  renderToastTitle: (finishedItemsCount: number) => string,
  isAsync?: boolean,
) {
  toast(
    ({ closeToast }) => (
      <BulkAction<Campaign>
        actionFn={actionFn}
        items={campaigns}
        onClose={closeToast}
        renderTitle={renderToastTitle}
        isAsync={isAsync}
        renderReviewItem={({ outreach_type, name, type }) => {
          const { icon: SubtypeIcon } =
            CAMPAIGN_OUTREACH_OPTIONS[outreach_type];

          const { label } = CAMPAIGN_TYPES[type];

          return (
            <div className="flex border border-black-200 p-3 gap-x-2 rounded-3xl">
              <CampaignTypeIcon
                type={type}
                variant={outreach_type === "drip" ? "drip" : "default"}
              />

              <div className="flex flex-col gap-y-1">
                <h5 className="line-clamp-1 break-all text-md-bold">{name}</h5>

                <div className="flex items-center gap-1">
                  <SubtypeIcon className="size-4 text-black-600" />
                  <span className="line-clamp-1 break-all text-button-xs text-black-500">
                    {label} • {outreach_type === "drip" ? "1st" : "2nd & 3rd+"}{" "}
                    connections
                  </span>
                </div>
              </div>
            </div>
          );
        }}
      />
    ),
    {
      position: "bottom-right",
      autoClose: false,
      closeButton: false,
      icon: false,
    },
  );
}
export function parseDripTargeting(dripTargeting: DripTargeting) {
  return typedObjectEntries(dripTargeting).reduce(
    (accumulator, [key, value]) => {
      if (key === "keywords") {
        accumulator[key] = value;
      } else if (key === "favorite") {
        accumulator[key] = value;
      } else {
        accumulator[key] = value.flatMap(({ id, name }) =>
          String(id)
            .split(",")
            .map((singleId) => ({ id: singleId.trim(), name })),
        );
      }
      return accumulator;
    },
    {} as DripTargeting,
  );
}
